import type { Worksheet, WorksheetAssignee } from '@mathflat/domain/Worksheet'

import { RouteName } from '@/router/RouteName'

/** 학생 정보 두 칸(studentWorksheetId·studentId)은 학생별 QR 에만 실린다. QR 원문에서 studentId 의 키는 relationId 다 */
export type WorksheetQrQuery = {
  worksheetId: Worksheet['id']
  dateTime: string
  studentWorksheetId?: WorksheetAssignee['studentWorksheetId']
  studentId?: WorksheetAssignee['studentId']
}

// 인쇄된 학습지 QR 은 학생 웹 해시 주소다. 새 앱 스캐너와 같이 link 주소도 학습지 QR 로 받는다
const STUDENT_WEB_HOST =
  /^student(-dev|-staging)?\.(mathflat\.com|mathflat\.co\.kr|schoolflat\.com)$/
const LINK_HOST = 'link.freewheelin.io'
const QR_PATH = '/qr'
// 앱 3종 스캐너와 같은 계약: 운영 `/qr`, dev `/_dev/qr`, staging `/_staging/qr`
const LINK_QR_PATH = /^(\/_dev|\/_staging)?\/qr$/

const toPositiveInt = (raw: string | null) => {
  if (raw === null) return undefined
  const value = Number(raw)
  return Number.isInteger(value) && value > 0 ? value : undefined
}

export const parseWorksheetQrQuery = (search: string): WorksheetQrQuery | null => {
  const params = new URLSearchParams(search)
  const worksheetId = toPositiveInt(params.get('worksheetId'))
  const dateTime = params.get('dateTime')
  if (!worksheetId || !dateTime) return null

  const studentWorksheetId = toPositiveInt(params.get('studentWorksheetId'))
  const studentId = params.get('relationId') || undefined
  return {
    worksheetId,
    dateTime,
    ...(studentWorksheetId && { studentWorksheetId }),
    ...(studentId && { studentId }),
  }
}

/** 학습지 QR 이 아니면 null — 다른 서비스의 QR, 교재 QR(`/signature-book/`) 모두 여기서 걸러진다 */
export const parseWorksheetQr = (text: string): WorksheetQrQuery | null => {
  let url: URL
  try {
    url = new URL(text.trim())
  } catch {
    return null
  }

  const host = url.hostname.toLowerCase()
  if (STUDENT_WEB_HOST.test(host)) {
    // 학생 웹 index.html 이 /#/qr 을 /qr 로 바꿔 주므로 해시가 풀린 주소도 같은 QR 이다
    if (url.hash.startsWith(`#${QR_PATH}?`)) {
      return parseWorksheetQrQuery(url.hash.slice(`#${QR_PATH}`.length))
    }
    if (url.pathname === QR_PATH) return parseWorksheetQrQuery(url.search)
    return null
  }
  if (host === LINK_HOST && LINK_QR_PATH.test(url.pathname)) {
    return parseWorksheetQrQuery(url.search)
  }
  return null
}

export const buildQrScoringPath = (query: WorksheetQrQuery) => {
  const params = new URLSearchParams({
    worksheetId: String(query.worksheetId),
    dateTime: query.dateTime,
  })
  if (query.studentWorksheetId) params.set('studentWorksheetId', String(query.studentWorksheetId))
  if (query.studentId) params.set('relationId', query.studentId)
  return `${RouteName.qrScoring}?${params.toString()}`
}

/**
 * 스쿨플랫 계정으로 매쓰플랫에 로그인하면 스쿨플랫으로 보낸다. QR 로 로그인 화면에 왔다면
 * 스쿨플랫에서도 같은 `/qr` 로 들어가야 로그인 뒤 채점 화면으로 이어진다.
 */
export const appendQrDestination = (baseUrl: string, loginSearch: string) => {
  const redirectPath = new URLSearchParams(loginSearch).get('redirectPathAfterLogin')
  if (!redirectPath) return baseUrl

  const [pathname, search = ''] = redirectPath.split('?')
  if (pathname !== RouteName.qrScoring) return baseUrl

  const query = parseWorksheetQrQuery(search)
  if (!query) return baseUrl
  return `${baseUrl.replace(/\/$/, '')}${buildQrScoringPath(query)}`
}
